import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    requestAnimationFrame(() => this.#filterVariantMedia());
  }

  #controller = new AbortController();

  /**
   * Hides media tagged for a different variant. The selected variant's featured
   * media is rendered first by Liquid, so it is the authoritative SKU source.
   * Uploaded fallback images may use a SKU-prefixed filename instead of the
   * standard `[variant:SKU]` alt tag.
   */
  #filterVariantMedia() {
    const firstImage = this.querySelector('img');
    const firstAlt = firstImage?.alt || '';
    const selectedSku =
      firstAlt.match(/\[variant:([^\]]+)\]/i)?.[1] || firstAlt.match(/^([A-Z]\d+[A-Z0-9]*)[-_\s]/i)?.[1];

    if (!firstImage) return;

    this.querySelectorAll('img[alt*="[variant:"]').forEach((image) => {
      const imageSku = image.alt.match(/\[variant:([^\]]+)\]/i)?.[1];
      if (!imageSku) return;

      const item = image.closest(
        '.product-media-container, .dialog-thumbnails-list__thumbnail, .product-media__drag-zoom-wrapper'
      );
      if (!(item instanceof HTMLElement)) return;

      // An untagged featured image belongs to the selected variant itself
      // (for example, a newly uploaded fallback image). In that case, hide
      // every tagged image because each one belongs to another variant.
      const visible = selectedSku ? imageSku === selectedSku : false;
      item.hidden = !visible;
      item.style.display = visible ? '' : 'none';
      item.setAttribute('aria-hidden', visible ? 'false' : 'true');
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
